// apps/web/src/features/clients/components/ClientDetailPage.tsx
//
// `/clients/:id` — the full aggregate. Layout is summary first, then Contacts
// and Legal entities as EQUAL sections (two columns on wide screens, stacked on
// narrow), then the version history, which loads only when opened (AC-5).
//
// INVARIANT: `canManage` folds permission and archive state together. An
// archived aggregate is read-only — the backend rejects child writes with
// `CLIENT_ARCHIVED_READONLY`, so the UI does not offer them at all.
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useArchiveClient, useClient, useRestoreClient } from '@magimp/shared-hooks';
import {
  Banner,
  Button,
  ConfirmModal,
  ErrorState,
  LoadingState,
  StatusBadge,
  useNarrow,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { clientsListReturnPath } from '../clientsListAnchor.js';
import { useTouchActionStyle, useTouchControlHeightScopeStyle } from '../useMobileFieldStyle.js';
import { ClientEditModal } from './ClientEditModal.js';
import { ClientSummary } from './ClientSummary.js';
import { ClientContactsSection } from './ClientContactsSection.js';
import { ClientLegalEntitiesSection } from './ClientLegalEntitiesSection.js';
import { ClientVersionsPanel } from './ClientVersionsPanel.js';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['clients', 'common']);
  const navigate = useNavigate();
  // Back returns to the result set the row was opened from (search, archive
  // scope, manager), which is also what keeps the list's return anchor valid.
  const backTo = clientsListReturnPath(useLocation().state);
  const show = useToastStore((s) => s.show);
  const perms = useAuthStore((s) => s.permissions);
  const canWrite = hasPermission(perms, 'clients.manage');
  const narrow = useNarrow();
  const actionStyle = useTouchActionStyle();
  const retryScope = useTouchControlHeightScopeStyle();
  const clientQ = useClient(id ?? null);
  const archive = useArchiveClient(id ?? '');
  const restore = useRestoreClient(id ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | 'archive' | 'restore'>(null);

  if (clientQ.isPending) {
    return <LoadingState />;
  }
  // INVARIANT: only a read with nothing to show becomes a page-level error.
  // Once the aggregate has been loaded, a failed BACKGROUND refresh must not
  // unmount this subtree: an open editor would lose the operator's draft, and
  // that draft may be the only record of a write whose outcome is unknown.
  if (clientQ.data === undefined) {
    return (
      // Wrapper, not a prop: `ErrorState` owns its retry `Button` and takes no
      // `style`. The scope raises the control token that button reads so the
      // only way back from a failed aggregate load is tappable (AC-12).
      <div style={retryScope}>
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(clientQ.error)}
          onRetry={() => void clientQ.refetch()}
        />
      </div>
    );
  }

  const c = clientQ.data;
  const isArchived = !!c.archivedAt;
  const canManage = canWrite && !isArchived;
  // One lifecycle write at a time: the second click would race the first and
  // the loser comes back as a conflict the operator never caused.
  const lifecycleBusy = archive.isPending || restore.isPending;

  const onArchive = () => {
    archive.mutate(undefined, {
      onSuccess: () => {
        show({ tone: 'success', text: t('clients:toasts.archived') });
        setConfirm(null);
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };
  const onRestoreClient = () => {
    restore.mutate(undefined, {
      onSuccess: () => {
        show({ tone: 'success', text: t('clients:toasts.restored') });
        setConfirm(null);
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-4)',
        minHeight: 0,
        flex: 1,
      }}
    >
      {/* Narrow: Back, then the client name on a line of its OWN, then the
          actions. Sharing one row squeezes a long name into a column barely
          wider than a word and pushes the actions off the first screen. */}
      <header
        style={{
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          alignItems: narrow ? 'stretch' : 'center',
          gap: 'var(--s-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Button
            icon="arrow-left"
            kind="ghost"
            style={actionStyle}
            data-testid="client-detail-back"
            onClick={() => navigate(backTo)}
          >
            {t('common:actions.back')}
          </Button>
        </div>
        <h1
          style={{
            fontSize: 'var(--fz-18)',
            fontWeight: 600,
            margin: 0,
            flex: narrow ? undefined : 1,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {c.name}
        </h1>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-3)',
            flexWrap: 'wrap',
          }}
        >
          {isArchived && <StatusBadge status="archived" />}
          {canManage && (
            <Button
              icon="pencil-line"
              kind="secondary"
              style={actionStyle}
              disabled={lifecycleBusy}
              onClick={() => setEditOpen(true)}
            >
              {t('common:actions.edit')}
            </Button>
          )}
          {canManage && (
            <Button
              icon="archive"
              kind="ghost"
              style={actionStyle}
              disabled={lifecycleBusy}
              data-testid="client-detail-archive"
              onClick={() => setConfirm('archive')}
            >
              {t('common:actions.archive')}
            </Button>
          )}
          {canWrite && isArchived && (
            <Button
              icon="rotate-ccw"
              kind="primary"
              style={actionStyle}
              disabled={lifecycleBusy}
              data-testid="client-detail-restore"
              onClick={() => setConfirm('restore')}
            >
              {t('common:actions.restore')}
            </Button>
          )}
        </div>
      </header>

      {/* The manage controls are GONE while archived, not merely disabled, so
          the page has to say why and what brings them back. */}
      {isArchived && canWrite && (
        <div data-testid="client-detail-archived-note">
          <Banner tone="info">{t('clients:blocked.archivedClient')}</Banner>
        </div>
      )}
      {lifecycleBusy && (
        <div data-testid="client-detail-lifecycle-pending">
          <Banner tone="info">{t('clients:blocked.pendingAction')}</Banner>
        </div>
      )}

      {clientQ.isError && (
        <div data-testid="client-detail-refresh-error">
          <Banner
            tone="warn"
            action={
              <Button
                size="sm"
                style={actionStyle}
                onClick={() => void clientQ.refetch()}
                disabled={clientQ.isFetching}
              >
                {t('common:actions.retry')}
              </Button>
            }
          >
            {t('clients:detail.refreshFailed')}
          </Banner>
        </div>
      )}

      <ClientSummary client={c} />

      {/* Contacts and Legal entities share the row: neither is a sub-detail of
          the other, and on narrow screens they stack in reading order. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--s-4)',
          alignItems: 'start',
        }}
      >
        {/* An archive/restore in flight is about to decide whether ANY child
            write is legal, so the sections stop offering them until it lands.
            Reading and copying stay available, and an open editor keeps its
            draft — only the controls that would start a new write are held. */}
        <ClientContactsSection
          clientId={c.id}
          clientName={c.name}
          contacts={c.contacts}
          canManage={canManage}
          busy={lifecycleBusy}
        />
        <ClientLegalEntitiesSection
          clientId={c.id}
          clientName={c.name}
          entities={c.legalEntities}
          canManage={canManage}
          busy={lifecycleBusy}
        />
      </div>

      <ClientVersionsPanel
        clientId={c.id}
        clientName={c.name}
        canManage={canManage && !lifecycleBusy}
      />

      {editOpen && <ClientEditModal client={c} onClose={() => setEditOpen(false)} />}
      {confirm === 'archive' && (
        <ConfirmModal
          danger
          narrowFullWidth
          title={t('clients:confirm.archiveTitle')}
          text={t('clients:confirm.archiveMessage', { name: c.name })}
          confirmLabel={t('common:actions.archive')}
          onConfirm={onArchive}
          onClose={() => setConfirm(null)}
        />
      )}
      {confirm === 'restore' && (
        <ConfirmModal
          narrowFullWidth
          title={t('clients:confirm.restoreTitle')}
          text={t('clients:confirm.restoreMessage', { name: c.name })}
          confirmLabel={t('common:actions.restore')}
          onConfirm={onRestoreClient}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
